import { Component, OnDestroy, OnInit } from "@angular/core";
import {
  NbMediaBreakpointsService,
  NbMenuService,
  NbSidebarService,
  NbThemeService,
} from "@nebular/theme";

import { filter, map, takeUntil } from "rxjs/operators";
import { Subject } from "rxjs";
import { AuthService } from "../../../modules/vnext/auth/auth-service.service";
import { HelpPanelService } from "../../../modules/help/help-panel.service";

@Component({
    selector: "ngx-header",
    styleUrls: ["./header.component.scss"],
    templateUrl: "./header.component.html",
    standalone: false
})
/**
 * Application header for the backoffice POS shell. Shows the current user,
 * theme picker, sidebar toggle, and the user menu (log out) sourced from Auth0.
 */
export class HeaderComponent implements OnInit, OnDestroy {
  private destroy$: Subject<void> = new Subject<void>();
  /** When true (viewport below the `xl` breakpoint) only the user picture is shown. */
  userPictureOnly: boolean = false;
  /** Currently signed-in Auth0 user profile. */
  user: any = {};

  /** User-context menu items rendered in the header dropdown. */
  items = [
    { title: 'Log out' },
  ];

  /** Selectable Nebular themes, including the A2Pay-branded palettes. */
  themes = [
    {
      value: "default",
      name: "Light",
    },
    {
      value: "dark",
      name: "Dark",
    },
    {
      value: "cosmic",
      name: "Cosmic",
    },
    {
      value: "corporate",
      name: "Corporate",
    },
    {
      value: "a2pay-red",
      name: "A2Pay Red",
    },
    {
      value: "a2pay-green",
      name: "A2Pay Green",
    },
  ];

  currentTheme = "default";

  //userMenu = [{ title: "Profile" }, { title: "Log out" }];

  constructor(
    private sidebarService: NbSidebarService,
    private menuService: NbMenuService,
    private themeService: NbThemeService,
    private authService: AuthService,
    private breakpointService: NbMediaBreakpointsService,
    private nbMenuService: NbMenuService,
    private helpPanel: HelpPanelService,
  ) {}

  ngOnInit() {
    this.currentTheme = this.themeService.currentTheme;

    this.nbMenuService.onItemClick()
      .pipe(
        filter((event) => event.tag === "user-menu"),
        map(({ item: { title } }) => title),
      )
      .subscribe(title => {
        if (title === "Log out") {
          this.authService.logout();
        }
      });

    this.authService.user$.subscribe((u) => (this.user = u));

    const { xl } = this.breakpointService.getBreakpointsMap();
    this.themeService
      .onMediaQueryChange()
      .pipe(
        map(([, currentBreakpoint]) => currentBreakpoint.width < xl),
        takeUntil(this.destroy$),
      )
      .subscribe(
        (isLessThanXl: boolean) => (this.userPictureOnly = isLessThanXl),
      );

    this.themeService
      .onThemeChange()
      .pipe(
        map(({ name }) => name),
        takeUntil(this.destroy$),
      )
      .subscribe((themeName) => (this.currentTheme = themeName));
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Switches the active Nebular theme. */
  changeTheme(themeName: string) {
    this.themeService.changeTheme(themeName);
  }

  /** Toggles the `menu-sidebar`; returns false to prevent default anchor navigation. */
  toggleSidebar(): boolean {
    this.sidebarService.toggle(true, "menu-sidebar");

    return false;
  }

  /** Opens or closes the help panel on the current screen's section of the user manual (work item 830). */
  toggleHelp(): void {
    this.helpPanel.toggle();
  }

  /** Navigates to the application home route; returns false to prevent default anchor navigation. */
  navigateHome() {
    this.menuService.navigateHome();
    return false;
  }
}
